import { Component } from '@theme/component';
import { onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  #abortController = new AbortController();

  #state = {
    activeItem: null,
  };

  /**
   * Tracks which `.menu-list__list-item` elements already have hover listeners
   * attached, so `#bindItems`/`refreshItems` can be called repeatedly (e.g. after
   * the menu content is swapped via `replaceChildren`) without double-binding.
   * @type {WeakSet<Element>}
   */
  #boundItems = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    // 保留点击外部关闭的功能
    document.addEventListener('click', this.#onBodyClick, {
      signal: this.#abortController.signal,
    });

    this.#bindItems();

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Attaches hover (`mouseenter`/`mouseleave`) listeners to every
   * `.menu-list__list-item` currently in this component that doesn't already
   * have them. Safe to call multiple times.
   */
  #bindItems() {
    const items = this.querySelectorAll('.menu-list__list-item');
    items.forEach((item) => {
      if (this.#boundItems.has(item)) return;
      this.#boundItems.add(item);

      item.addEventListener('mouseenter', (event) => this.onMouseEnter(event, item), {
        signal: this.#abortController.signal,
      });
      item.addEventListener('mouseleave', () => this.onMouseLeave(), {
        signal: this.#abortController.signal,
      });
    });
  }

  /**
   * Re-scans this component for `.menu-list__list-item` elements and binds
   * hover listeners to any that were added after `connectedCallback` ran
   * (e.g. content swapped in via `replaceChildren`). Already-bound items are
   * skipped, so this is safe to call any number of times.
   */
  refreshItems() {
    this.#bindItems();
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  /**
   * 【修改】activate 方法
   * 改为 Hover 模式后，点击不再负责开关菜单。
   * 这里去掉了 preventDefault，点击标题链接将直接跳转页面。
   */
  activate = (event) => {
    // 如果你依然希望点击没有任何反应，可以把下面这行解开：
    // event.preventDefault();
  };

  /**
   * 【新增】鼠标移入打开
   */
  onMouseEnter = (event, item) => {
    this.open(item, event);
  };

  /**
   * 【新增】鼠标移出关闭
   */
  onMouseLeave = () => {
    this.close();
  };

  open = (item, event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    
    if (this.#state.activeItem && this.#state.activeItem !== item) {
      this.#state.activeItem.classList.remove('active');
    }

    this.#state.activeItem = item;
    item.classList.add('active');
    
    const isDefaultSlot = event && event.target && event.target.slot === '';
    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;
    
    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }
    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  close = () => {
    const item = this.#state.activeItem;
    if (!item) return;

    // 强制移除焦点，解决 Header 颜色不恢复的问题
    const link = item.querySelector('.menu-list__link') || item.querySelector('a');
    if (link) link.blur();
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();

    item.classList.remove('active');
    this.style.setProperty('--submenu-height', '0px');
    this.#state.activeItem = null;
  };

  #onBodyClick = (event) => {
    // 点击组件外部才关闭
    if (this.contains(event.target)) return;
    
    if (this.#state.activeItem) {
      this.close();
    }
  };

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element.closest('.menu-list__list-item');
}

function findSubmenu(element) {
  const submenu = element?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}